import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Loader2 } from 'lucide-react';

function formatCurrency(amount, symbol = 'R') {
  return `${symbol}${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function InvoiceCreateDialog({ 
  open, onOpenChange, clients, tiers, settings, onSubmit 
}) {
  const [form, setForm] = useState({
    client_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    include_vat: true,
    tax_rate: settings?.vat_rate || 15,
    discount_amount: 0,
    billing_period_start: '',
    billing_period_end: '',
    notes: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    billing_tier_id: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f,
        tax_rate: settings?.vat_rate || 15,
        due_date: '',
      }));
    }
  }, [open, settings]);

  const updateLineItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, line_items: items };
    });
  };

  const addLineItem = () => {
    setForm(f => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0 }] }));
  };

  const removeLineItem = (idx) => {
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  };

  const applyTier = (tierId) => {
    const tier = tiers.find(t => t.id === tierId);
    if (tier) {
      setForm(f => ({
        ...f,
        billing_tier_id: tierId,
        line_items: [{
          description: `${tier.name} — Monthly Subscription`,
          quantity: 1,
          unit_price: tier.monthly_price,
        }]
      }));
    }
  };

  const subtotal = form.line_items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
  const taxAmount = form.include_vat ? (subtotal - (parseFloat(form.discount_amount) || 0)) * ((parseFloat(form.tax_rate) || 0) / 100) : 0;
  const total = subtotal - (parseFloat(form.discount_amount) || 0) + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        client_id: form.client_id,
        invoice_date: form.invoice_date || undefined,
        due_date: form.due_date || undefined,
        include_vat: form.include_vat,
        tax_rate: parseFloat(form.tax_rate) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        billing_period_start: form.billing_period_start || undefined,
        billing_period_end: form.billing_period_end || undefined,
        notes: form.notes || undefined,
        billing_tier_id: form.billing_tier_id || undefined,
        line_items: form.line_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0),
          tax_rate: form.include_vat ? parseFloat(form.tax_rate) || 0 : 0,
          tax_amount: form.include_vat ? ((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0)) * ((parseFloat(form.tax_rate) || 0) / 100) : 0,
        })),
      };
      await onSubmit(payload);
      setForm({
        client_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '',
        include_vat: true, tax_rate: settings?.vat_rate || 15, discount_amount: 0,
        billing_period_start: '', billing_period_end: '', notes: '',
        line_items: [{ description: '', quantity: 1, unit_price: 0 }], billing_tier_id: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-wider">Create New Invoice</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Client & Tier */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Client <span className="text-red-500">*</span></Label>
              <Select value={form.client_id} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Apply Billing Tier</Label>
              <Select value={form.billing_tier_id} onValueChange={applyTier}>
                <SelectTrigger data-testid="tier-select"><SelectValue placeholder="Optional: auto-fill from tier" /></SelectTrigger>
                <SelectContent>
                  {tiers.filter(t => t.is_active).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} — {formatCurrency(t.monthly_price)}/mo</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} data-testid="invoice-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="due-date" placeholder="Auto-calculated if empty" />
            </div>
          </div>

          {/* Service Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Service Period Start</Label>
              <Input type="date" value={form.billing_period_start} onChange={e => setForm(f => ({ ...f, billing_period_start: e.target.value }))} data-testid="period-start" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Service Period End</Label>
              <Input type="date" value={form.billing_period_end} onChange={e => setForm(f => ({ ...f, billing_period_end: e.target.value }))} data-testid="period-end" />
            </div>
          </div>

          {/* VAT Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Include VAT</Label>
              <p className="text-xs text-slate-500 mt-0.5">{form.include_vat ? `VAT at ${form.tax_rate}% will be added` : 'No VAT will be charged'}</p>
            </div>
            <Switch checked={form.include_vat} onCheckedChange={v => setForm(f => ({ ...f, include_vat: v }))} data-testid="vat-toggle" />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Line Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addLineItem} className="text-xs">
                <Plus className="h-3 w-3 mr-1" />Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    {idx === 0 && <Label className="text-[10px] text-slate-500">Description</Label>}
                    <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" data-testid={`line-desc-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <Label className="text-[10px] text-slate-500">Qty</Label>}
                    <Input type="number" min="1" step="1" value={item.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} data-testid={`line-qty-${idx}`} />
                  </div>
                  <div className="col-span-3">
                    {idx === 0 && <Label className="text-[10px] text-slate-500">Unit Price (R)</Label>}
                    <Input type="number" step="0.01" value={item.unit_price} onChange={e => updateLineItem(idx, 'unit_price', e.target.value)} data-testid={`line-price-${idx}`} />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {form.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-500 hover:text-red-700 p-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Discount (R)</Label>
              <Input type="number" step="0.01" value={form.discount_amount} onChange={e => setForm(f => ({ ...f, discount_amount: e.target.value }))} data-testid="discount-input" />
            </div>
            {form.include_vat && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">VAT Rate (%)</Label>
                <Input type="number" step="0.5" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: e.target.value }))} data-testid="vat-rate" />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes for the invoice" data-testid="invoice-notes" />
          </div>

          {/* Totals Summary */}
          <div className="bg-slate-900 text-white rounded-lg p-4 space-y-1">
            <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
            {parseFloat(form.discount_amount) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">Discount</span><span className="text-red-400">-{formatCurrency(parseFloat(form.discount_amount))}</span></div>}
            {form.include_vat && <div className="flex justify-between text-sm"><span className="text-slate-400">VAT ({form.tax_rate}%)</span><span>{formatCurrency(taxAmount)}</span></div>}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700"><span>Total</span><span>{formatCurrency(total)}</span></div>
          </div>

          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!form.client_id || form.line_items.every(i => !i.description) || loading} data-testid="create-invoice-btn">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Create Invoice'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceCreateDialog;
